import { useCallback, useEffect, useRef, useState } from 'react';
import { InvitationError, getInvitationPreview } from '../../services/invitations';
import type { AcceptedInvitation, InvitationPreview } from '../../types/invitations';

/**
 * The accept-invitation page's state vocabulary, and the hook that drives the first half of it.
 *
 * Unions rather than a pile of booleans: "loading and expired and accepted" is not a state and
 * should not be representable. The page renders `outcome ?? status`, so a submission result replaces
 * the preview wholesale instead of coexisting with it.
 */

/** Where the invitation preview got to. */
export type PreviewStatus =
  | { phase: 'loading' }
  /** No token in the fragment, or the server refused it. One state, on purpose. */
  | { phase: 'invalid' }
  | { phase: 'paused'; message: string }
  /**
   * The server could not answer. The invitation itself is untouched.
   *
   * `canRetry` is false for a rate limit: the limiter is shared between the preview and accept
   * endpoints, so offering a button there spends one of the invitee's remaining attempts on finding
   * out they have none.
   */
  | { phase: 'unavailable'; message: string; canRetry: boolean }
  | { phase: 'form'; preview: InvitationPreview };

/** How a submission ended, when it ended the page rather than leaving the form up. */
export type AcceptInviteOutcome =
  | { phase: 'accepted'; accepted: AcceptedInvitation }
  | { phase: 'registered'; message: string; email: string }
  | { phase: 'invalid' }
  | { phase: 'paused'; message: string };

const statusForError = (error: unknown): PreviewStatus => {
  if (error instanceof InvitationError) {
    if (error.kind === 'paused') {
      return { phase: 'paused', message: error.message };
    }

    // A server that could not answer leaves the invitation usable, so this is not the dead-link
    // state. Telling someone their link is dead when the server hiccuped costs them the invitation.
    if (error.kind === 'unavailable' || error.kind === 'rate_limited') {
      return {
        phase: 'unavailable',
        message: error.message,
        canRetry: error.kind !== 'rate_limited',
      };
    }
  }

  return { phase: 'invalid' };
};

/**
 * Loads the invitation preview, with a retry for the states that are worth retrying.
 *
 * The request counter is load-bearing rather than defensive: `StrictMode` double-invokes this
 * effect in development, so two previews really are in flight at once and the slower one must not
 * overwrite the newer.
 */
export const useInvitationPreview = (token: string | null) => {
  const [status, setStatus] = useState<PreviewStatus>(() =>
    token ? { phase: 'loading' } : { phase: 'invalid' }
  );
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    if (!token) return;

    const requestId = ++requestIdRef.current;
    setStatus({ phase: 'loading' });

    try {
      const preview = await getInvitationPreview(token);
      if (requestIdRef.current === requestId) {
        setStatus({ phase: 'form', preview });
      }
    } catch (error: unknown) {
      if (requestIdRef.current === requestId) {
        setStatus(statusForError(error));
      }
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { status, reload };
};
